import { useEffect, useRef, useState, useCallback, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import ReflectionPage from "./pages/ReflectionPage";
import HistoryPage from "./pages/HistoryPage";
import ProfilePage from "./pages/ProfilePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import FavoritesPage from "./pages/FavoritesPage";
import PremiumPage from "./pages/PremiumPage";
import BirthChartPage from "./pages/BirthChartPage";
import WeeklyForecastPage from "./pages/WeeklyForecastPage";
import MonthlyForecastPage from "./pages/MonthlyForecastPage";
import CompatibilityPage from "./pages/CompatibilityPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import ChatPage from "./pages/ChatPage";
import TarotPage from "./pages/TarotPage";
import WellnessPage from "./pages/WellnessPage";
import SharedReadingPage from "./pages/SharedReadingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FriendsPage from "./pages/FriendsPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add auth token from localStorage as fallback for cookie issues
api.interceptors.request.use((config) => {
  // Admin routes use admin_token
  if (config.url?.startsWith("/admin/") && config.url !== "/admin/login") {
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
  } else {
    const token = localStorage.getItem("soulguide_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("soulguide_token");
    setUser(null);
  };

  // Helper to store token from login/register response
  const storeToken = (token) => {
    if (token) {
      localStorage.setItem("soulguide_token", token);
    }
  };

  const value = {
    user,
    setUser,
    loading,
    checkAuth,
    logout,
    storeToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, storeToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];

        try {
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          const response = await api.post("/auth/session", { session_id: sessionId });
          const userData = response.data.user;
          const token = response.data.token;
          storeToken(token);
          setUser(userData);

          // Check if onboarding is complete
          if (userData.onboarding_complete) {
            navigate("/dashboard", { replace: true, state: { user: userData } });
          } else {
            navigate("/onboarding", { replace: true, state: { user: userData } });
          }
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#070B14' }}>
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-white/70 font-body">Connecting to your guidance...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#070B14' }}>
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If user passed from AuthCallback via state, use that
  if (location.state?.user) {
    return children;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check if onboarding is needed
  if (!user.onboarding_complete && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously - prevents race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reflection"
        element={
          <ProtectedRoute>
            <ReflectionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/favorites"
        element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium"
        element={
          <ProtectedRoute>
            <PremiumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/success"
        element={
          <ProtectedRoute>
            <PremiumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/features"
        element={
          <ProtectedRoute>
            <PremiumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/birth-chart"
        element={
          <ProtectedRoute>
            <BirthChartPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/weekly-forecast"
        element={
          <ProtectedRoute>
            <WeeklyForecastPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/compatibility"
        element={
          <ProtectedRoute>
            <CompatibilityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/monthly-forecast"
        element={
          <ProtectedRoute>
            <MonthlyForecastPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium/tarot"
        element={
          <ProtectedRoute>
            <TarotPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wellness"
        element={
          <ProtectedRoute>
            <WellnessPage />
          </ProtectedRoute>
        }
      />
      <Route path="/shared/:shareId" element={<SharedReadingPage />} />
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <FriendsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Register service worker & capture install prompt
  useEffect(() => {
    // Service worker registration
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }

    // Capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show banner after 5 seconds if not already installed
      setTimeout(() => setShowInstallBanner(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setInstallPrompt(null);
  };

  return (
    <div className="App min-h-screen" style={{ background: '#070B14' }}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
          {/* PWA Install Banner */}
          {showInstallBanner && installPrompt && (
            <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-[60] animate-fade-in-up" data-testid="install-banner">
              <div className="bg-[#0A101D] border border-[#D4AF37]/30 rounded-2xl p-4 shadow-2xl shadow-[#D4AF37]/10">
                <div className="flex items-start gap-3">
                  <img src="/icon-192.png" alt="SoulGuide" className="w-10 h-10 rounded-xl" />
                  <div className="flex-1">
                    <p className="text-white font-display text-sm">Install SoulGuide</p>
                    <p className="text-white/50 text-xs font-body mt-0.5">Add to your home screen for the best experience</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleInstall}
                    data-testid="install-btn"
                    className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#A16207] text-[#070B14] font-body font-semibold text-sm py-2 rounded-xl"
                  >
                    Install
                  </button>
                  <button
                    onClick={() => setShowInstallBanner(false)}
                    className="px-4 text-white/40 text-sm font-body hover:text-white/60"
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          )}
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
